/**
 * Placeholder shapes for a screen whose data has not arrived.
 *
 * ## Why this replaces a spinner
 *
 * The console's loading state was a centred spinner that took over the whole page. That is
 * slower than it needs to be in two separate ways, and only one of them is about milliseconds.
 *
 * The first is real: a full-page swap means the browser tears down the current layout, paints a
 * spinner, then builds the new page from nothing. Every navigation flashes empty. Keeping the
 * frame and filling only the content means the header, the nav and the page title are already
 * there and never move.
 *
 * The second is perception, and it is the larger of the two. A spinner says "something is
 * happening" and nothing else. A skeleton says "a table with six columns is about to appear
 * here", so the reader has already started orienting - where the amount column will be, roughly
 * how many rows - before the data lands. The same latency reads as faster because the wait is
 * doing work.
 *
 * ## Why the shapes are approximate on purpose
 *
 * A skeleton that pretends to know the answer is worse than one that does not. These draw the
 * *structure* - a table has rows, a card has lines - and never a plausible-looking number or a
 * row count taken from the last render. An operator who reads a shape as content, even for a
 * frame, has been told something false about money.
 *
 * ## Motion
 *
 * One shimmer, defined once in the stylesheet, and it stops entirely under
 * `prefers-reduced-motion`. A page of independently pulsing blocks is worse than a static one:
 * the eye tracks the movement instead of the structure, which is the opposite of the point.
 */

/**
 * One placeholder block.
 *
 * `width` takes any CSS length so a caller can vary the run lengths. Varying them matters more
 * than it sounds: a column of identical bars reads as a rendering artefact, and slightly
 * uneven ones read as text.
 */
export function SkeletonBar({
  width = '100%',
  height = 14,
}: {
  readonly width?: string;
  readonly height?: number;
}): JSX.Element {
  return <span className="skel" style={{ width, height }} aria-hidden="true" />;
}

/**
 * A table's shape, while the rows are on their way.
 *
 * `columns` and `rows` describe the layout only. The row count is a guess at a comfortable
 * screenful and never the size of the answer, which nobody knows yet.
 */
export function SkeletonTable({
  columns = 5,
  rows = 6,
  label = 'Loading',
}: {
  readonly columns?: number;
  readonly rows?: number;
  /** Announced to a screen reader, which gets a word rather than a wall of empty cells. */
  readonly label?: string;
}): JSX.Element {
  return (
    <div className="table-wrap" role="status" aria-busy="true" aria-label={label}>
      <table className="table skel-table" aria-hidden="true">
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            <tr key={row}>
              {Array.from({ length: columns }, (_, column) => (
                <td key={column}>
                  {/*
                    Uneven widths, derived from the position rather than randomised. Random
                    widths would change on every render and make the placeholder itself flicker;
                    a deterministic pattern is uneven enough to read as text and stays put.
                  */}
                  <SkeletonBar width={`${String(45 + ((row * 7 + column * 13) % 45))}%`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A stack of lines, for a card or a detail panel. */
export function SkeletonLines({
  lines = 3,
  label = 'Loading',
}: {
  readonly lines?: number;
  readonly label?: string;
}): JSX.Element {
  return (
    <div className="skel-lines" role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: lines }, (_, i) => (
        <SkeletonBar key={i} width={`${String(60 + ((i * 17) % 35))}%`} />
      ))}
    </div>
  );
}

/**
 * The stat tiles at the top of a dashboard.
 *
 * Its own shape rather than `SkeletonLines`, because these are the first thing on the page and
 * a row of tiles is instantly recognisable. Getting the count right is what stops the page
 * jumping when the real ones arrive.
 */
export function SkeletonStats({ count = 3 }: { readonly count?: number }): JSX.Element {
  return (
    <div className="grid grid--stats" role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: count }, (_, i) => (
        <div className="stat" key={i}>
          <SkeletonBar width="55%" height={11} />
          <SkeletonBar width="70%" height={24} />
          <SkeletonBar width="40%" height={11} />
        </div>
      ))}
    </div>
  );
}
